import {first, isEmpty} from 'lodash';
import {Logger} from '../utils/Logger';
import {Component} from '@angular/core';
import {Validator} from '../utils/Validator';
import {FormGroupUtil} from '../utils/FormGroupUtil';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {Keys, StorageService} from '../service/storage.service';
import {LoanDetailsService} from '../service/loan-details.service';
import {LenderDetailsService} from '../service/lender-details.service';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {AnalyticsTracker} from '../utils/AnalyticsTracker';
import {UserService} from '../service/user.service';
import {executeWithLoader, LoaderStatus} from '../utils/loader.util';

@Component({
    selector: 'app-residence-info',
    templateUrl: './residence-info.component.html',
    styleUrls: ['../loan-application-flow-component/application-flow.css', './residence-info.component.scss']
})
export class ResidenceInfoComponent {

    public readonly AadhaarProofType = 'aadhaar';
    public readonly FormControlNameProofNumber = 'proofNumber';
    public backLink: string;
    public forwardLink: string;
    public applicationFlowType: ApplicationFlowType;
    public readonly residenceInfoFormGroup: FormGroup;
    public readonly proofTypes = [
        {value: this.AadhaarProofType, displayName: 'Masked e-Aadhaar'},
        {value: 'passport', displayName: 'Passport'},
        {value: 'drivinglicense', displayName: 'Driving License'},
        {value: 'voterid', displayName: 'Voter ID'},
        {value: 'electricity', displayName: 'Electricity Bill'},
        {value: 'telephone', displayName: 'Telephone Bill - BSNL and MTNL only'},
        {value: 'lpg', displayName: 'LPG Bill'}
    ];
    public aadharLinkAnswers: any = [
        {value: true, display: 'Yes, it is linked', id: 'a1'},
        {value: false, display: 'No, it is not linked', id: 'a2'},
    ];
    isFormSubmitted = false;
    private readonly router: Router;
    private readonly storageService: StorageService;
    private readonly ProofNumberFormControl: FormControl;
    private readonly loanDetailsService: LoanDetailsService;
    private readonly lenderDetailsService: LenderDetailsService;
    private readonly userService: UserService;
    loaderStatus = new LoaderStatus();

    constructor(formBuilder: FormBuilder, loanDetailsService: LoanDetailsService, lenderDetailsService: LenderDetailsService,
                storageService: StorageService, activatedRoute: ActivatedRoute, router: Router, public analyticsTracker: AnalyticsTracker, userService: UserService) {
        this.router = router;
        this.storageService = storageService;
        this.loanDetailsService = loanDetailsService;
        this.lenderDetailsService = lenderDetailsService;
        this.userService = userService;
        this.ProofNumberFormControl = formBuilder.control(null,
            [Validators.required, Validator.notEmpty, Validators.maxLength(20), Validators.pattern(/^[a-zA-Z0-9-\/ ]+$/)]);

        this.residenceInfoFormGroup = formBuilder.group({
            proofType: formBuilder.control(null, Validators.required),
            proofNumber: this.ProofNumberFormControl,
        });

        activatedRoute.data.subscribe((data: Data) => {
            this.onLinksReceived(data.links);
            this.applicationFlowType = data.applicationFlowType;
            if (this.applicationFlowType === ApplicationFlowType.LoanApplicationFlow) {
                this.residenceInfoFormGroup.addControl('aadhaarLinkedWithMobile',
                    formBuilder.control(undefined, Validators.required));
            }
            this.onResidenceInformationReceived(data.residenceInformation);
        });
    }

    private static allErrorMessages() {
        return {
            proofType: [{
                type: 'required',
                message: 'Please select a proof type'
            }],
            proofNumber: [{
                type: 'required',
                message: 'Please enter a proof number'
            }, {
                type: 'maxlength',
                message: 'Please reduce the length of the proof number'
            }, {
                type: 'pattern',
                message: 'Please enter a valid proof number'
            }, {
                type: 'empty',
                message: 'Please enter a proof number'
            }],
            aadhaarLinkedWithMobile: [{
                type: 'required',
                message: 'Please select one of the options'
            }]
        };
    }

    public getErrorMessage(control: AbstractControl): string {
        const controlName = this.getControlName(control);
        const errors = ResidenceInfoComponent.allErrorMessages();
        const message = '';
        if (control.errors) {
            const occurredError = first(Object.keys(control.errors));
            const predefinedErrorsForControl = errors[controlName];
            const result = predefinedErrorsForControl.find(predefinedError => {
                return predefinedError.type === occurredError;
            });
            if (result) {
                return result.message;
            } else {
                Logger.log(`Could not find error message for error: ${occurredError}`);
            }
        }
        return message;
    }

    public async onSubmit(formGroup: FormGroup): Promise<void> {
        this.isFormSubmitted = true;
        FormGroupUtil.validateFormGroup(formGroup);
        if (!formGroup.valid) {
            return;
        }

        await executeWithLoader(this.loaderStatus, async () => {
            const proofType = formGroup.controls.proofType.value;
            const proofNumber = formGroup.controls.proofNumber ? formGroup.controls.proofNumber.value : undefined;
            switch (this.applicationFlowType) {
                case ApplicationFlowType.LoanApplicationFlow:
                    const aadhaarLinkedWithMobile = formGroup.controls.aadhaarLinkedWithMobile.value;
                    const loanId = this.storageService.getItem(Keys.LOAN_ID);
                    const userId = this.storageService.getItem(Keys.PROFILE_ID);
                    await this.loanDetailsService.updateResidenceProof(proofType, proofNumber, loanId, 60);
                    await this.userService.captureAadhaarLinkedWithMobileAnswer(userId, aadhaarLinkedWithMobile);
                    break;
                case ApplicationFlowType.LenderApplicationFlow:
                    const lenderId = this.storageService.getItem(Keys.LENDER_ID);
                    await this.lenderDetailsService.updateResidenceProof(proofType, proofNumber, lenderId, 33);
                    break;
                default:
                    throw new Error(`Encountered an unknown application flow type [${this.applicationFlowType}].`);
            }

            await this.navigateToNextStep();
        });
    }

    public getPlaceholderForProofNumber(formGroup: FormGroup) {
        let proofName = 'proof';
        const selectedProofType = formGroup.controls.proofType.value;
        if (selectedProofType) {
            const {displayName} = this.proofTypes.find(proofType => proofType.value === selectedProofType);
            proofName = displayName;
        }
        return `Enter ${proofName} number`;
    }

    public onResidenceInformationReceived(residenceInfo: { proofType: string, proofNumber: string, aadhaarLinkedWithMobile: boolean } | null): void {
        if (residenceInfo) {
            this.residenceInfoFormGroup.controls.proofType.setValue(residenceInfo.proofType);
            this.residenceInfoFormGroup.controls.proofNumber.setValue(residenceInfo.proofNumber);
            if (this.residenceInfoFormGroup.controls.aadhaarLinkedWithMobile) {
                this.residenceInfoFormGroup.controls.aadhaarLinkedWithMobile.setValue(residenceInfo.aadhaarLinkedWithMobile);
            }
            this.onProofTypeChanged(this.residenceInfoFormGroup);
        }
    }

    public onLinksReceived({back, forward}: { back: string, forward: string }) {
        if (!isEmpty(back)) {
            this.backLink = back;
        }
        if (!isEmpty(forward)) {
            this.forwardLink = forward;
        }
    }

    public resetProofNumber(formGroup: FormGroup) {
        formGroup.controls.proofNumber.reset();
    }

    public onProofTypeChanged(formGroup: FormGroup) {
        if (formGroup.controls.proofType.value === this.AadhaarProofType) {
            this.resetProofNumber(formGroup);
            formGroup.removeControl(this.FormControlNameProofNumber);
        } else if (!formGroup.contains(this.FormControlNameProofNumber)) {
            formGroup.addControl(this.FormControlNameProofNumber, this.ProofNumberFormControl);
            this.resetProofNumber(formGroup);
        }
    }

    private getControlName(c: AbstractControl): string | null {
        const formGroup = c.parent.controls;
        return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
    }

    private async navigateToNextStep() {
        await this.router.navigate([this.forwardLink]);
    }
}

export enum ApplicationFlowType {
    LoanApplicationFlow,
    LenderApplicationFlow
}
